import {
    ArrowDropDownCircleTwoTone,
    BuildTwoTone,
    ConstructionTwoTone,
    CorporateFareTwoTone,
    CropFreeTwoTone,
    DomainTwoTone,
    FoundationTwoTone,
    LocalShippingTwoTone,
    MeetingRoomTwoTone,
    ParkTwoTone,
    PlaceTwoTone,
    Public,
    ScienceTwoTone,
} from '@mui/icons-material';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ListOfValuesReadDTO, LocationDTO, LocationTypeCode } from '../../api/dto';
import { getLocationChildren, getLocationRoots, LocationWithNodeDTO } from '../../api/locations';
import { NoPaddingTooltip } from '../../components/layout/elements/NoPaddingTooltip';
import { LocationInfoCard } from '../../components/LocationInfoCard';
import { useMapDialogContext } from '../../components/MapDialogContext';
import { FilterTypeEnum } from '../../components/searching/filters';
import { TooltipIcon } from '../../components/tables/table/utils';
import { TreeWidget } from '../../components/tree/TreeWidget';
import { useDeviceType } from '../../hooks/useDeviceType';
import { fetchLocationsByName } from '../../lib/api';
import { getLocationLabel } from '../../lib/label';
import { areLocationsEqual, isLeafLocation } from '../../lib/records/location';
import { FetchUseCase } from '../fields/AsyncAutocompleteElement';
import { AsyncAutocompleteFormElement, TFilterElement, TFormElement } from '../types';
import { UiSchemaType } from '../UiSchemaType';

export const LOCATION_TYPE_ICON: Record<LocationTypeCode, ReactElement | undefined> = {
    [LocationTypeCode.BARAQUE]: <FoundationTwoTone />,
    [LocationTypeCode.BUILDING]: <DomainTwoTone />,
    [LocationTypeCode.BUILDING_PARTITION]: <DomainTwoTone />,
    [LocationTypeCode.CUSTOM_EXPERIMENT]: <ScienceTwoTone />,
    [LocationTypeCode.CUSTOM_ZONE_SECURITY]: undefined,
    [LocationTypeCode.CUSTOM_POINT_GAS]: undefined,
    [LocationTypeCode.UNDERGROUND_EQUIPMENT]: <ArrowDropDownCircleTwoTone />,
    [LocationTypeCode.GREEN_SPACE]: <ParkTwoTone />,
    [LocationTypeCode.FLOOR]: <CorporateFareTwoTone />,
    [LocationTypeCode.GROUPING_FACILITY]: <PlaceTwoTone />,
    [LocationTypeCode.GROUPING_FACILITY_NODE]: <CorporateFareTwoTone />,
    [LocationTypeCode.ROOM]: <MeetingRoomTwoTone />,
    [LocationTypeCode.MACHINE]: <BuildTwoTone />,
    [LocationTypeCode.STRUCTURE]: <ConstructionTwoTone />,
    [LocationTypeCode.SITE]: <DomainTwoTone />,
    [LocationTypeCode.ROAD]: <LocalShippingTwoTone />,
    [LocationTypeCode.ZONE]: <CropFreeTwoTone />,
};

const locationTypeProperties = (
    allowAnyLocation: boolean = false,
    restrictToFacility: boolean = false,
    tooltip?: string,
    watchPaths?: string[] | ((path: string) => string[])
): AsyncAutocompleteFormElement['typeProperties'] => ({
    watchPaths,
    fetchOptions: (searchTerm, exactId, idField, values) =>
        fetchLocationsByName({
            searchTerm,
            selectable: !allowAnyLocation,
            facilityCode: restrictToFacility && values ? values[0]?.code : undefined,
        }),
    fetchUseCases: [FetchUseCase.ON_KEYSTROKE],
    getOptionLabel: getLocationLabel,
    renderTagChip: (chipProps, value) => (
        <NoPaddingTooltip title={<LocationInfoCard location={value} />}>
            <span>
                <Chip {...chipProps} />
            </span>
        </NoPaddingTooltip>
    ),
    autocompleteProps: {
        isOptionEqualToValue: areLocationsEqual,
        forcePopupIcon: false,
    },
    endAdornment: ({ field, multiple, matchId, updateValue = () => {}, disabled, readOnly }) => {
        const intl = useIntl();
        const formContext = useFormContext();
        const paths = typeof watchPaths === 'function' ? watchPaths(field.name) : watchPaths;
        const facility: ListOfValuesReadDTO | undefined =
            formContext !== null && paths ? formContext.watch(paths)[0] : paths?.map(() => null);
        const facilityCode = facility?.code;
        const { value: currentLocationValue, name } = field;
        const { openDialog } = useMapDialogContext();
        const { isMobile } = useDeviceType();
        const selectedNodes: LocationWithNodeDTO[] = currentLocationValue
            ? multiple
                ? currentLocationValue
                : [currentLocationValue]
            : [];
        return (
            <>
                <TreeWidget
                    title={intl.formatMessage({ id: 'document.location.tooltip' })}
                    disabled={disabled || readOnly}
                    TreeProps={{
                        loadRootNodes: (initialIds) => getLocationRoots({ ids: initialIds, facilityCode }),
                        loadChildrenNodes: (parent: LocationWithNodeDTO) => getLocationChildren({ parent: parent }),
                        getNodeLabel: (location: LocationWithNodeDTO) => (
                            <Stack
                                direction={isMobile ? 'column' : 'row'}
                                spacing={isMobile ? 0 : 1}
                                alignItems={isMobile ? 'left' : 'center'}
                            >
                                <Typography noWrap>{location.impactName}</Typography>
                            </Stack>
                        ),
                        getNodeId: (location: LocationWithNodeDTO) => String(location.id),
                        getParent: (location: LocationWithNodeDTO) => String(location.node.parentId),
                        isRootNode: (location: LocationWithNodeDTO) => location.node.nodeLevel === 0,
                        isLeafNode: isLeafLocation,
                        selectedNodes,
                        onSelect: (locations: LocationWithNodeDTO[]) => {
                            if (!locations) return;
                            if (locations.length === 0) {
                                updateValue && updateValue(null);
                                return;
                            }
                            if (locations.length === 1 && !multiple) {
                                updateValue && updateValue(matchId ? locations[0].id : locations[0]);
                            } else {
                                updateValue &&
                                    updateValue(matchId ? locations.map((e: LocationWithNodeDTO) => e.id) : locations);
                            }
                        },
                        keys: [
                            'locations-picker',
                            name,
                            String(facilityCode),
                            ...selectedNodes.map((node: LocationWithNodeDTO) => String(node.id)),
                        ],
                        isSelectable: (location) => allowAnyLocation || location.selectable,
                        multiSelect: multiple,
                        tooltipContent: (location) => <LocationInfoCard location={location} />,
                        icon: (location) => LOCATION_TYPE_ICON[location.locationType],
                    }}
                />
                {!multiple && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <NoPaddingTooltip
                            title={currentLocationValue ? <LocationInfoCard location={currentLocationValue} /> : null}
                        >
                            {/* To not make the IconButton take full width and display the shadow weirdly it should be
                wrapped in a Box. display flex and alignItems center make sure that the icon is displayed
                centered and not on top */}
                            <span>
                                <IconButton
                                    onClick={() => currentLocationValue && openDialog(currentLocationValue)}
                                    size="small"
                                    disabled={!currentLocationValue || disabled}
                                    sx={{ my: -1, ml: 0.5 }}
                                >
                                    <Public />
                                </IconButton>
                            </span>
                        </NoPaddingTooltip>
                    </Box>
                )}
                {tooltip && <TooltipIcon tooltip={tooltip} />}
            </>
        );
    },
});

interface LocationSchemaParameters extends Pick<AsyncAutocompleteFormElement, 'path' | 'label' | 'required'> {
    disabled?: boolean;
    multiple?: boolean;
    allowAnyLocation?: boolean;
    restrictToFacility?: boolean;
    watchPaths?: string[] | ((path: string) => string[]);
    tooltip?: string;
}

export const locationSchema = ({
    path,
    label,
    required,
    disabled,
    multiple,
    allowAnyLocation,
    restrictToFacility,
    tooltip,
    watchPaths,
}: LocationSchemaParameters): TFormElement => ({
    type: UiSchemaType.AUTOCOMPLETE_ASYNC,
    path,
    label,
    required,
    disabled,
    typeProperties: { ...locationTypeProperties(allowAnyLocation, restrictToFacility, tooltip, watchPaths), multiple },
});

interface LocationFilterSchemaParameters extends Pick<TFilterElement, 'id' | 'label'> {}

export const locationFilterSchema = ({ id, label }: LocationFilterSchemaParameters): TFilterElement => ({
    type: FilterTypeEnum.HIERARCHICAL_AUTOCOMPLETE_ASYNC,
    id,
    label,
    valueGetter: (value: LocationDTO) => value.id,
    typeProperties: locationTypeProperties(true),
});
